import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { faBars } from '@fortawesome/free-solid-svg-icons';

import { SideNavComponent } from './../side-nav/side-nav.component';
import { UserDetailService } from 'src/app/shared/services/user-detail.service';
import { AuthService } from '../../auth/auth.service';
import { AppState } from 'src/app/state/app.reducer';
import { AuthActions } from '../../auth/state/action-typs';
import { ResetDashboardStateAction } from '../../dashboard/state/action-types';
import { Menu } from 'src/app/types';
import { MenuService } from '../menu.service';
import { ThemeService } from '../theme.service';
import { NavService } from '../nav.service';
import { HelpPanelService } from 'src/app/shared/components/help-panel/help-panel.service';
import { ThemeToggleComponent } from '../theme-toggle.component';
import { UiToggleComponent } from '../ui-toggle.component';
import { MobileNavComponent } from '../mobile-nav/mobile-nav.component';
import { GetLocalStorageValues } from 'src/app/shared/util/local-storage';
import { AiAssistantComponent } from 'src/app/shared/components/ai-assistant/ai-assistant.component';
import { ConfirmDialogComponent } from 'src/app/shared/components/competitions/dialogs/confirm-dialog/confirm-dialog.component';

interface AppNavigationBridge {
  postMessage(message: 'home'): void;
}

type AppWebViewWindow = Window & {
  AppNavigation?: AppNavigationBridge;
};

@Component({
  selector: 'app-navbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ThemeToggleComponent, UiToggleComponent, MobileNavComponent, AiAssistantComponent, ConfirmDialogComponent],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  protected readonly menuService = inject(MenuService);
  protected readonly navService = inject(NavService);
  protected readonly router = inject(Router);
  protected readonly store = inject(Store<AppState>);
  protected readonly themeService = inject(ThemeService);
  protected readonly userDetailService = inject(UserDetailService);
  protected readonly helpPanelService = inject(HelpPanelService);
  protected readonly authService = inject(AuthService);
  protected readonly showLogoutConfirm = signal(false);
  protected readonly destroy$ = new Subject<void>();
  protected readonly ls = new GetLocalStorageValues();
  private resolvingLogo = false;

  // Constants
  protected readonly faBars = faBars;

  // Inputs
  readonly sideNavComponent = input<SideNavComponent>(undefined as any);
  readonly showNav = input<Observable<boolean>>(undefined as any);

  // State signals
  protected readonly clubEmailId = signal('');
  protected readonly clubName = signal('');
  protected readonly isAiAssistantOpen = signal(false);
  protected readonly askApCollapsed = signal(false);
  private askApTimer: any;
  protected readonly isBookingTabActive = signal(false);
  protected readonly isDarkMode = signal(false);
  protected readonly isMobileNavOpen = signal(false);
  protected readonly isSideNavCollapsed = signal(false);
  protected readonly isSuperUserLoggedIn = signal(false);
  protected readonly menus = signal<Menu[]>([]);
  protected readonly parentClubLogo = signal('assets/img/activitypro-logo-dark.png');
  private readonly FALLBACK_LOGO = 'assets/img/activitypro-logo-dark.png';
  protected readonly roleType = signal('');
  protected readonly isAdminAppWebView = signal(this.detectAdminAppWebView());

  protected readonly showSuperBanner = computed(() =>
    localStorage.getItem('superUser') === 'true'
  );

  protected readonly bannerText = computed(() => {
    const email = localStorage.getItem('impersonatedEmail');
    const name = localStorage.getItem('impersonatedName');
    if (name || email) {
      const parts: string[] = [];
      if (name) parts.push(name);
      if (email) {
        const [local, domain] = email.split('@');
        parts.push(local.slice(0, 2) + '***@' + (domain || ''));
      }
      return 'Impersonating: ' + parts.join(' • ');
    }
    return 'Logged in as Super User';
  });

  ngOnInit(): void {
    this.getClubDetails();
    this.showNav()?.pipe(takeUntil(this.destroy$)).subscribe();
    this.askApTimer = setTimeout(() => { this.askApCollapsed.set(true); }, 2000);

    this.menuService.menus$.pipe(takeUntil(this.destroy$)).subscribe(menus => {
      this.menus.set(menus);
    });

    this.userDetailService.loggedOnUserDetails$.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {
      if (data) {
        this.parentClubLogo.set(data?.parentClubLogo || this.FALLBACK_LOGO);
        if (!data?.parentClubLogo) {
          this.resolveClubLogo(data);
        }
        this.clubName.set(localStorage.getItem('clubName') || data?.name || '');
        this.roleType.set(data?.roleType ?? '');
        this.isBookingTabActive.set(data?.isAllowForBooking ?? false);
        if (data?.roleType) {
          this.menuService.setRoleType(data.roleType, data?.isAllowForBooking ?? false);
        }
        // Re-read club name after a short delay (in case it was set by login flow after this fires)
        setTimeout(() => {
          const clubName = localStorage.getItem('clubName');
          if (clubName) this.clubName.set(clubName);
        }, 500);
      }
    });

    this.isSuperUserLoggedIn.set(localStorage.getItem('superUser') === 'true');
    this.userDetailService.isSuperUserLoggedIn.pipe(takeUntil(this.destroy$)).subscribe(isSuper => {
      this.isSuperUserLoggedIn.set(isSuper);
    });

    this.themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe(theme => {
      this.isDarkMode.set(theme === 'dark');
    });

    this.navService.isSideNavCollapsed$.pipe(takeUntil(this.destroy$)).subscribe(isCollapsed => {
      this.isSideNavCollapsed.set(isCollapsed);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected closeMobileNav(): void {
    this.isMobileNavOpen.set(false);
  }

  protected logout(): void {
    this.showLogoutConfirm.set(true);
  }

  protected performLogout(): void {
    this.showLogoutConfirm.set(false);
    this.userDetailService._loggeedOnUserSubject.next(null);
    localStorage.clear();
    this.store.dispatch(AuthActions.logout({ authUser: undefined }));
    this.store.dispatch(ResetDashboardStateAction.resetDashBoardState());
  }

  protected toggleSidebarCollapse(): void {
    if (window.innerWidth > 981) {
      const next = !this.isSideNavCollapsed();
      this.isSideNavCollapsed.set(next);
      this.navService.toggleSideNavCollapse(next);
    } else {
      this.isMobileNavOpen.update(v => !v);
    }
  }

  protected onActionButtonClick(): void {
    this.isAiAssistantOpen.set(true);
  }

  protected collapseAskAp(): void {
    this.askApTimer = setTimeout(() => { this.askApCollapsed.set(true); }, 2000);
  }

  protected toggleHelpPanel(): void {
    this.helpPanelService.toggle();
  }

  protected closeAiAssistant(): void {
    this.isAiAssistantOpen.set(false);
  }

  protected navigateHomeFromApp(): void {
    const appNavigation = (window as AppWebViewWindow).AppNavigation;
    if (appNavigation?.postMessage) {
      appNavigation.postMessage('home');
      return;
    }

    window.history.back();
  }

  private detectAdminAppWebView(): boolean {
    if (localStorage.getItem('AP_ADMIN_APP_WEBVIEW') === 'true') return true;

    const userAgent = navigator.userAgent;
    const isAndroidWebView = /Android/i.test(userAgent) &&
      (/;\s*wv\)/i.test(userAgent) || /Version\/\d+(?:\.\d+)*\s+Chrome\//i.test(userAgent));
    const isIosDevice = /iPhone|iPad|iPod/i.test(userAgent);
    const isIosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const isIosWebView = isIosDevice && /AppleWebKit/i.test(userAgent) &&
      !/Safari\//i.test(userAgent) && !isIosStandalone;

    return isAndroidWebView || isIosWebView;
  }

  /**
   * The club logo is captured into the `USER` blob at login time. Any session that
   * logged in before that assignment existed — or via a path that skipped it — is
   * stuck with an empty value until the user clears storage and logs in again.
   * Recover by reading the club row directly (`ParentClubAppIconURL`), then push the
   * value back through the shared stream so the side-nav picks it up too, and persist
   * it so later navigations do not refetch.
   */
  private resolveClubLogo(user: any): void {
    if (this.resolvingLogo) return;
    const parentClubId = this.ls.getPostgreParentClubId();
    if (!parentClubId) return;

    this.resolvingLogo = true;
    this.authService.getParentClubByPostgreId(parentClubId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (club: any) => {
          const logo = club?.ParentClubAppIconURL || club?.parentClubAppIconURL
            || club?.data?.ParentClubAppIconURL || club?.data?.parentClubAppIconURL || '';
          if (!logo) return;
          this.parentClubLogo.set(logo);
          const updated = { ...user, parentClubLogo: logo };
          localStorage.setItem('USER', JSON.stringify(updated));
          this.userDetailService.setUserDetails(updated);
        },
        error: () => { this.resolvingLogo = false; },
      });
  }

  /**
   * The club logo is a remote CDN URL, so it can fail for reasons the app cannot see
   * (403 on a referer-restricted distribution, deleted object, DNS). Without this the
   * `<img>` just renders empty and looks identical to "no logo configured", which makes
   * the failure impossible to distinguish. Fall back to the brand mark and say why.
   */
  protected onLogoError(): void {
    const failed = this.parentClubLogo();
    if (failed === this.FALLBACK_LOGO) return;
    console.warn('[navbar] Club logo failed to load, falling back to brand mark. URL:', failed);
    this.parentClubLogo.set(this.FALLBACK_LOGO);
  }

  private getClubDetails(): void {
    const user = this.ls.getUserDetails();
    if (user?.emailId) {
      this.clubEmailId.set(user.emailId);
    }
  }
}
